const post = {
  slug: "instagram-dm-automation-checklist",
  title: "Instagram DM Automation Checklist: 15 Checks Before You Go Live",
  seoTitle: "Instagram DM Automation Checklist (15 Checks)",
  description:
    "Before you switch on Instagram DM automation, run through these 15 checks on your catalog, payments, policies, triggers and handover so launch day goes smoothly.",
  excerpt:
    "Most automation problems come from a missing detail, not a broken tool. Use this checklist to set up your catalog, payments, triggers and handover before real customers arrive.",
  category: "Automation",
  tags: ["Instagram DM automation checklist", "Instagram automation setup", "Before going live Instagram automation"],
  date: "2026-11-07T12:00:00Z",
  cover: {
    src: "/blog-images/instagram-dm-automation-checklist-cover.jpg",
    alt: "A phone showing a checklist for launching Instagram DM automation: catalog, payments, test order and handover",
  },
  blocks: [
    {
      type: "p",
      text:
        "Switching on automation feels like the finish line. It is really the start line. The first real customer will test every detail you skipped: a missing size, a wrong price, a payment you forgot to set up. A short checklist before launch costs you an hour and saves you from the awkward messages that follow.",
    },
    {
      type: "p",
      text:
        "This list works for any Instagram DM automation tool, with Checkout DM examples where they help. If you are still choosing a tool, read [what Instagram DM automation is](/blog/what-is-instagram-dm-automation) and our [comparison of DM automation tools](/blog/instagram-dm-automation-feature-comparison) first.",
    },

    { type: "h2", text: "Part 1: Connection and safety" },
    {
      type: "ol",
      items: [
        "**Connect through Instagram’s official login.** If a tool asks for your Instagram password, stop. Our guide to [whether DM automation is safe](/blog/is-instagram-dm-automation-safe-2026) explains why.",
        "**Use a professional Instagram account.** Business tools connect to professional accounts.",
        "**Check the permissions you approved.** You should recognise each one, and you can remove the tool’s access from your Instagram settings at any time.",
      ],
    },

    { type: "h2", text: "Part 2: Catalog" },
    {
      type: "ol",
      start: 4,
      items: [
        "**Add clear photos, names and prices.** The customer should be able to tell products apart without asking. Our [catalog tips](/blog/instagram-catalog-tips-photos-sizes) help here.",
        "**Add every size and set the stock for each size.** Then a size that sells out is not offered again.",
        "**Write one clear description per product.** Include material, fit and care, so fewer questions reach you.",
      ],
    },

    { type: "h2", text: "Part 3: Payments and shipping" },
    {
      type: "ol",
      start: 7,
      items: [
        "**Choose how you get paid.** UPI, QR code or bank transfer needs your confirmation. Razorpay, Stripe or PayPal confirms automatically. See [UPI versus a payment gateway](/blog/accept-payments-instagram-upi-vs-gateway).",
        "**Plan how you will check manual payments.** Decide when you will compare screenshots with your bank account, and read about [fake payment screenshots](/blog/avoid-fake-payment-screenshots-instagram-sellers).",
        "**Set your shipping fee, or a free-shipping rule.** Decide what the customer pays, so it is added at checkout.",
      ],
    },

    { type: "h2", text: "Part 4: Policies and answers" },
    {
      type: "ol",
      start: 10,
      items: [
        "**Write your return and exchange policy.** It is shown to customers in DMs automatically, so make it clear. See [how to write a return policy](/blog/return-policy-for-instagram-shop).",
        "**Add your FAQs.** Cover delivery time, sizing and payment, the questions you answer most often.",
      ],
    },

    { type: "h2", text: "Part 5: Triggers and testing" },
    {
      type: "ol",
      start: 12,
      items: [
        "**Choose specific trigger words.** A word like “yes” fires by accident. Pick something short and distinct, and tell people exactly what to type.",
        "**Place a full test order from a second Instagram account.** Go through product choice, size, address and payment as a customer would, and see what the order looks like on your side.",
      ],
    },
    {
      type: "chat",
      shop: "yourshop",
      title: "A test order",
      caption: "Illustrative example. Test from a second account before you tell customers.",
      messages: [
        { from: "time", text: "Test from your second Instagram account" },
        { from: "buyer", text: "Hi" },
        { from: "shop", text: "Welcome! Here is what we have. Which product would you like?" },
        { from: "buyer", text: "Linen Kurta Set" },
        { from: "shop", text: "Which size and how many would you like?" },
      ],
    },

    { type: "h2", text: "Part 6: Handover and limits" },
    {
      type: "ol",
      start: 14,
      items: [
        "**Plan who handles chats that need a person.** On Checkout DM Starter, Growth and Pro, the Stuck tab lists them, and you can take over any chat. Read [what to automate and what to keep human](/blog/what-to-automate-in-instagram-dms).",
        "**Know your plan limits before a launch.** On the Free plan, new customer conversations pause once you reach the monthly limit of 15, so upgrade before a big post or sale. See the [pricing page](/pricing).",
      ],
    },

    { type: "h2", text: "Mistakes that cause the most trouble" },
    {
      type: "ul",
      items: [
        "**Launching with half a catalog.** A customer who picks a product with no price or sizes gets stuck, and so do you.",
        "**Skipping the second-account test.** You will find a gap in minutes that a customer would find in front of your whole audience.",
        "**Using a trigger word people type naturally.** Everyday words fire by accident and fill your inbox with conversations nobody wanted.",
        "**Not saying what happens next.** Tell customers how long delivery takes and how to reach you, so they do not message to ask.",
        "**Promoting before you are ready.** A Reel that goes well can bring more conversations in a day than a small plan allows. Check your limits first.",
      ],
    },
    {
      type: "p",
      text:
        "None of these takes long to fix, and all of them are easier to fix before customers are watching. Keep this list open on launch day and tick each item off as you go.",
    },

    { type: "h2", text: "The first week after launch" },
    {
      type: "ul",
      items: [
        "**Check your orders daily.** Confirm each one looks right and that payments match.",
        "**Look at where chats get stuck.** A repeated reason points to a gap in your catalog or FAQs.",
        "**Check your automations.** See how many messages each rule has sent, and adjust the wording if replies are low.",
        "**Ask a few customers how it felt.** A short message gets you honest feedback.",
        "**Change one thing at a time.** Then you can tell what worked.",
      ],
    },
    {
      type: "p",
      text:
        "Once the first week is calm, you can add more rules and posts. See the full flow on the [Instagram DM automation page](/features/instagram-dm-automation).",
    },
  ],
  related: [
    { to: "/features/instagram-dm-automation", title: "Instagram DM automation", description: "The full chat flow from first message to order." },
    { to: "/features/instagram-product-catalog-checkout", title: "Catalog and checkout", description: "Products, stock, payment and delivery details." },
    { to: "/pricing", title: "Checkout DM pricing", description: "Check plan limits before a launch." },
  ],
  faqs: [
    {
      q: "What should I check before turning on Instagram DM automation?",
      a: "Check your connection and permissions, your catalog and stock, your payment method, your return policy and FAQs, your trigger words, and run a full test order from a second account.",
    },
    {
      q: "How do I test Instagram DM automation safely?",
      a: "Use a second Instagram account to message your business, comment your trigger word and go through a complete order, including payment, before you tell real customers.",
    },
    {
      q: "What goes wrong most often on launch day?",
      a: "Missing sizes or stock, a payment method that is not set up, a vague trigger word and no plan for who handles chats that need a person.",
    },
    {
      q: "How long does setup take?",
      a: "With your products ready, connecting your account and setting up a test order can be done in a short session. Preparing photos, prices and policies usually takes longer than the tool itself.",
    },
    {
      q: "What happens if I reach the Free plan limit?",
      a: "On Checkout DM’s Free plan, new customer conversations pause once you reach 15 unique customer conversations in a month, until the limit resets or you upgrade.",
    },
  ],
};

export default post;
